import styled from '@emotion/styled'
import { t } from 'i18next'
import { useAtomValue, useSetAtom } from 'jotai'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { Text } from '../../../../common/components/Typography/Typography'
import { generalErrorMessageAtom, isFromAppAtom } from '../../../../state/common/atoms'
import { useIsMobile } from '../../../hooks/useBreakpoint'
import useChangeLanguage from '../../../hooks/useChangeLanguage'
import useLoginState from '../../../hooks/useLoginState'
import { useModals } from '../../../hooks/useModals'
import NavBar from '../../NavBar/NavBar'
import ModalActionButtons from '../ModalActionButtons/ModalActionButtons'

const Container = styled.div`
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const InnerContainer = styled.div<{ isMobile: boolean }>`
  margin-top: ${(props) => (props.isMobile ? '2rem' : '4.5rem')};
  padding: 0 ${(props) => (props.isMobile ? '1.5rem' : '0')};
  margin-bottom: 1.5rem;
  gap: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 31.25rem;
  text-align: center;
`

const StyledH1 = styled.h1`
  font-size: 1.25rem;
  font-weight: 22rem;
  line-height: 1.75rem;
  text-align: center;
`

interface FullScreenModalProps {
  modalId: string
  onClose: () => void
  onContinue: () => void
  onDigitalClinic?: (queue: string) => void
  onNode?: (nodeId: string) => void
}

const FullScreenModal: React.FC<FullScreenModalProps> = ({
  modalId,
  onClose,
  onContinue,
  onDigitalClinic,
  onNode,
}) => {
  const isFromApp = useAtomValue(isFromAppAtom)
  const setGeneralErrorMessage = useSetAtom(generalErrorMessageAtom)
  const sessionExpiredCallback = useCallback(
    () => setGeneralErrorMessage(t('common.sessionExpired')),
    [setGeneralErrorMessage]
  )
  const { loginStatus, logout } = useLoginState(sessionExpiredCallback)
  const { changeLanguage } = useChangeLanguage()

  const { modals, getModal } = useModals()

  const isMobile = useIsMobile()

  const modal = useMemo(() => {
    const modalData = modals.find((m) => m.type === modalId)
    return getModal(modalData?.id ?? '')
  }, [getModal, modals, modalId])

  const headerRef = useRef<HTMLHeadingElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const headerId = 'full-screen-notification-header-id'
  const contentId = 'full-screen-notification-content-id'

  const focusOnButtons = modal?.actions && modal.actions.length > 0

  useEffect(() => {
    if (focusOnButtons) {
      buttonRef.current?.focus()
    } else {
      headerRef.current?.focus()
    }
  })

  return (
    <Container>
      {!isFromApp && (
        <NavBar
          onLanguageSelect={changeLanguage}
          loginStatus={loginStatus}
          logoutCallBack={logout}
          variant="detached"
        />
      )}
      <InnerContainer isMobile={isMobile}>
        <StyledH1 ref={focusOnButtons ? undefined : headerRef} id={headerId}>
          {modal?.title}
        </StyledH1>
        <Text $size={isMobile ? 300 : 400} $height="Large" $weight="Regular" id={contentId}>
          {modal?.content}
        </Text>
      </InnerContainer>
      {modal?.actions && (
        <ModalActionButtons
          actions={modal?.actions}
          onClose={onClose}
          onContinue={onContinue}
          onDigitalClinic={onDigitalClinic}
          onNode={onNode}
          ref={focusOnButtons ? buttonRef : undefined}
          aria-labelledby={headerId}
          aria-describedby={contentId}
        />
      )}
    </Container>
  )
}

export default FullScreenModal
